/**
 * SpanReplaceTrigger.tsx — PLAT-10214 (T6, epic PLAT-10209) · PLAT-10616 (T13)
 *
 * The floating affordance for a translated-text selection, anchored above a
 * resolved contiguous-run selection in the translated pane. Originally a single
 * "Fix Translation" button; it is now a context menu whose rows the host gates:
 *
 *   - **Fix Translation** — opens the `ReplaceSpanEditor` (always shown).
 *   - **Remove Translation** — blanks the span's translation, leaving the
 *     source untouched (shown only when `onRemove` is wired: the review
 *     workspace, per the host).
 *   - **Re-dub AI Voice** — re-synthesizes the dub for just the selected span
 *     (shown only when `onReDub` is wired: a dubbing-enabled translated
 *     language, per the host).
 *
 * Placement mirrors the editor's — `position: fixed`, above the anchor word,
 * clamped to the viewport, and PORTALED TO THE BODY like the editor: rendered
 * inline it is a descendant of the review, and on the clip / reel entry the
 * review sits inside the clip preview, whose `backdrop-filter` makes `fixed`
 * resolve against the modal rather than the viewport — so viewport coordinates
 * put the menu a screen away from the selection (CLAUDE.md pitfall 19). The
 * z-index is the editor's, so it clears the clip-preview stack the same way.
 * `onMouseDown` is suppressed on the surface so clicking an item doesn't
 * collapse the text selection before its handler runs.
 */

import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import DeleteIcon from '@src/svgs/DeleteIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

// Menu footprint used for viewport clamping (min-width 180 + padding). Height
// is per-row, since the two optional rows are independently gated by the host.
const MENU_WIDTH = 200;
const ROW_HEIGHT = 40;
const MENU_PADDING = 4;

export interface SpanReplaceTriggerProps {
  /** The selection's first translated word — the menu anchors above it. */
  anchorElement: HTMLElement;
  /** Open the Fix Translation editor for the pending span. */
  onFixTranslation: () => void;
  /**
   * Remove the selected span's translation (PLAT-11340 follow-up) — authors an
   * empty override, so the source words keep playing and only the translated
   * text goes. Wired by the review workspace; absent elsewhere.
   */
  onRemove?: () => void;
  /**
   * Re-dub the selected span (PLAT-10616). When absent, only Fix Translation
   * shows — degrading to the pre-T13 single-action affordance.
   */
  onReDub?: () => void;
}

export const SpanReplaceTrigger: React.FC<SpanReplaceTriggerProps> = ({
  anchorElement,
  onFixTranslation,
  onRemove,
  onReDub,
}) => {
  // Compute placement once on mount from the anchor's rect (the selection is
  // stable while the menu shows). `position: fixed` ⇒ viewport coords.
  const rowCount = 1 + (onRemove ? 1 : 0) + (onReDub ? 1 : 0);
  const [placement] = useState(() =>
    computePlacement(
      anchorElement,
      MENU_WIDTH,
      rowCount * ROW_HEIGHT + MENU_PADDING * 2,
    ),
  );
  return ReactDOM.createPortal(
    <Surface
      placement={placement}
      // Suppress mousedown so the selection isn't cleared before a click reads
      // it (the editor pre-fills from the resolved model, but keeping the
      // selection avoids a flash where the menu vanishes mid-click).
      onMouseDown={e => e.preventDefault()}
      data-testid="span-replace-menu"
      role="menu"
    >
      <ContextMenuItem
        type="button"
        role="menuitem"
        onClick={onFixTranslation}
        data-testid="span-replace-trigger"
        aria-label="Fix translation for the selected words"
      >
        <SimplePencilIcon />
        Fix Translation
      </ContextMenuItem>
      {onRemove && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          $danger
          onClick={onRemove}
          data-testid="span-remove-trigger"
          aria-label="Remove the translation for the selected words"
        >
          <DeleteIcon strokeColor="currentColor" />
          Remove Translation
        </ContextMenuItem>
      )}
      {onReDub && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          onClick={onReDub}
          data-testid="span-redub-trigger"
          aria-label="Re-dub the selected words with an AI Voice"
        >
          <MicrophoneIcon fillColor="currentColor" />
          Re-dub AI Voice
        </ContextMenuItem>
      )}
    </Surface>,
    document.body,
  );
};

function computePlacement(
  anchor: HTMLElement,
  width: number,
  height: number,
): { left: number; top: number } {
  const rect = anchor.getBoundingClientRect();
  const MARGIN = 6;
  // Prefer above the selection; drop below when there's no room above.
  const openBelow = rect.top < height + MARGIN;
  const top = openBelow ? rect.bottom + MARGIN : rect.top - height - MARGIN;
  const clamped = clampMenuToViewport(rect.left, top, width, height);
  return { left: clamped.x, top: clamped.y };
}

const Surface = styled(ContextMenuSurface).attrs(
  (props: { placement: { left: number; top: number } }) => props,
)`
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  /* On the body now: lift above the clip preview's own stack, as the span
     editor does, instead of the bare menu z the inline host got by. */
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );
`;
